import store from "../../redux-store";
import { arrayToMap, getChannelData } from "../../helpers/catalogueHelpers";
import { cloneDeep } from "lodash";
import { convertArrayToObject } from "../../helpers/commonHelpers";
import { notifyError, notifySuccess } from "../../effects-bridge";
import { backendErrorMap } from "../../utils/backendErrors";
import { ORDER_CLOSED, REST_SALE_ITEMS_ON_HOLD } from "../../utils/constants/shared";
import { instoreLocalQuery } from "../../myOrders/buckets";
import { fetchOthersOffline, filterRemoteStore } from "../../helpers/OfflineHelper/shared";
import { cancelSaleOffline, customerSearchOffline, logoutOffline, saveSaleOffline, voidSaleOffline, updateFulfillmentStatusOffline, fetchOfflineSale, saleCloseOffline } from "../../helpers/OfflineHelper";
import { getCustomerDetailsUsingMobile } from "../../redux-store/slices/CustomerSlice";
import { logoutUser } from "../../redux-store/slices/LoginSlice";
import { cancelOrder, closeOrder, print, printSuccess } from "../../redux-store/slices/TableViewSlice";
import {
  voidOrder,
  updateOrderStatus,
  getOpenOrderData,
  getOpenOrderDataSuccess,
  getSearchOrders,
  getSearchOrdersSuccess,
  saveMyorder,
  fetchOrderData,
} from "../../redux-store/slices/MyOrderSlice";
import { updateDiscountClosed } from "../../redux-store/slices/DiscountSlice";
import { fetchAllPrinter, storeAllPrinters } from "../../redux-store/slices/PrinterSlice";
import { holdSaleData } from "../../redux-store/slices/SaleSlice";
import {
  catalogueDataCallComplete,
  getCatalogueData,
  updateCatalogueData,
} from "../../redux-store/slices/catalogueSlice";


/**
 * Uplifted pieces of `facade/ServiceFacade` — the COMMON half.
 *
 * This folder MIRRORS the app file it came from (web 846 lines, mobile 992) and is seeded with
 * only the symbols needed so far. The rest of that file — the sale/table/ticket flows, the
 * printing paths — is still in the apps; it reaches printers, IndexedDB and `@rista/ui`.
 *
 * 🔴 **This file exists because the folder was `.desktop`-ONLY, which was hazard 32 and a
 * standing violation of ledger rule 4.** The old comment justified that with "mobile has no such
 * file"; mobile's is 992 lines with 56 importers. The folder now has all three files.
 *
 * ⚠️ The store reads go through the STORE BRIDGE rather than taking state as an argument. That
 * is the sanctioned exception (ledger, 2026-08-19): `isOffline` is called from long-lived
 * singletons and from web's axios request interceptor, which has no caller to pass state to.
 *
 * ⚠️ **The split-folder imports above are BARE, which is what a `shared.ts` must do** (hazard 16). It matters most for `arrayToMap`, which genuinely differs between the two
 * platform variants — one returns `null` for an empty input, the other an empty object — and
 * pinning a variant here would bind the wrong one on the other platform.
 * `helpers/OfflineHelper` is the exception, deep-imported to its LEAF `shared`: `fetchOthersOffline`
 * is defined there rather than in either variant, and routing through the leaf keeps this file out
 * of the variants' own import chains (the rule saga/printer/shared.ts records).
 *
 * ⚠️ `catalogueSlice` imports `othersDataTypes` back out of this file, so the two modules form an
 * IMPORT CYCLE. That is not new and it is safe: the cycle already exists in shipped web code
 * (`apps/web/src/redux-store/slices/catalogueSlice.ts` ← `facade/ServiceFacade`, since the
 * original monorepo import), and neither side touches the other's binding at module-eval time —
 * `othersDataTypes` is only read inside a reducer body, and the action creators only inside a
 * function body.
 */

/**
 * Whether this terminal is running in offline mode. The gate every feature path checks before
 * deciding between the API and the IndexedDB-backed offline layer (CLAUDE.md §7).
 *
 * ⚠️ Both apps keep a debug override commented out directly above the return — one returns true,
 * the other false. **Neither is carried over.** A comment cannot change behaviour, and
 * reproducing them here would invite someone to uncomment one in the SHARED module, where it
 * would silently flip every consumer on both platforms.
 */
export function isOffline() {
  return store.getState().businessStore?.isOffline;
}

/**
 * Look a customer up: `sale/customer/filter`, through the customer saga (`getCustomerInfo`),
 * which ALSO stores the first result as the current customer (`getCustomerRecord`) and its loyalty
 * view. Offline there is no lookup and the callback never runs (`customerSearchOffline`).
 *
 * Moved 2026-09-29 for My Orders v2's shared customer lookup: the two apps' bodies were
 * token-identical; only their offline helpers differ, and each variant of `OfflineHelper` keeps
 * its own.
 */
export function customerSearch(ip: Record<string, unknown>, callback: (response: any) => void = () => {}): void {
  if (isOffline()) {
    customerSearchOffline();
    return;
  }
  store.dispatch(getCustomerDetailsUsingMobile({ data: ip, callback }));
}

/**
 * Whether a LAN sale sync is in flight — either about to start or actively running. Web's request
 * interceptor uses it to decide whether an offline request is aborted outright
 * (`AbortSignal.abort()`) or allowed through while the sync drains.
 *
 * ⚠️ `localServer` is mounted in BOTH stores (web `redux-store/index.ts:77`, mobile
 * `reduxStore/index.ts:81`) and mobile's own copy reads exactly the same two fields, so nothing
 * here is web-specific.
 */
export function isSyncStarted() {
  return store.getState()?.localServer?.isSaleSyncStarted || store.getState()?.localServer?.isSaleSyncing;
}

/**
 * Drives the generic "others" catalogue fetch: for each entry, `key` is read off the API
 * response and `transform` shapes it before it lands on `state[type]`. The channelvos entry is
 * the odd one out — its transform also writes `state.channelData` as a side effect, which is why
 * it takes `(d, state)` while every other transform takes `(d)`.
 *
 * 🔴 **This table moved here from `index.desktop.ts` in the batch that unified
 * `getCatalogueDataFn`** (below). It had been web-only because only web drove its catalogue
 * refetch through one generic loop; the other platform's copy of that loop referenced this table
 * without ever defining it, which is the defect that decision repaired. See `getCatalogueDataFn`.
 *
 * ⚠️ `memberships` is the one entry whose `type` is not a declared field on either platform's
 * `CatalogState` — `updateCatalogueData` creates it dynamically. Preserved as-is; it behaves
 * exactly as it always has on web.
 */
export const othersDataTypes = [
  { type: "brandsData", key: "brands", transform: (d: any) => d || [] },
  { type: "categoryData", key: "categories", transform: (d: any) => convertArrayToObject(d || [], "mid") },
  { type: "charges", key: "charges", transform: (d: any) => arrayToMap(d || [], "mid") },
  { type: "taxArea", key: "taxAreas", transform: (d: any) => d[0] || {} },
  { type: "taxTypes", key: "taxTypes", transform: (d: any) => d || [] },
  {
    type: "channelvos",
    key: "channelvos",
    transform: (d: any, state: any) => {
      state.channelData = getChannelData(state.catalogueData);
      return d || [];
    }
  },
  { type: "channelDef", key: "channelDefs", transform: (d: any) => d || [] },
  { type: "categoryGroup", key: "categoryGroups", transform: (d: any) => d || [] },
  { type: "menuData", key: "menus", transform: (d: any) => arrayToMap(d || [], "mid") },
  { type: "scheduleData", key: "itemSchedules", transform: (d: any) => d || [] },
  { type: "memberships", key: "memberships", transform: (d: any) => d || [] },
];

/**
 * Refetch the whole "others" catalogue: online it fires the one catalogue API action, offline it
 * replays every `othersDataTypes` entry out of the local store and folds each into state.
 *
 * 🔴 **This is the ONE function in this batch that is a DELIBERATE BEHAVIOUR CHANGE, decided by
 * the user on 2026-08-23, and it is a repair.** The two apps had different implementations of
 * this function, and only one of them worked:
 * - web's is the body below, and it has always run;
 * - the other app's was a truncated copy of it that referenced `othersDataTypes` and
 *   `updateCatalogueData` **without importing or defining either**, so its offline branch threw
 *   `ReferenceError` every time it was reached (measured session 26: live callers exist, and the
 *   Metro bundle carried exactly one occurrence of the undefined name — the use site).
 *
 * The earlier decision was to PRESERVE that failure rather than switch on a never-executed path.
 * The user overruled it: adopt web's version on both platforms. The truncated copy is plainly an
 * abandoned transcription of this one — same loop, same shape, missing only the two symbols and
 * the completion dispatch — so this restores what its author intended rather than inventing a
 * design. **What it changes on the previously-broken platform: an offline catalogue refetch stops
 * throwing and starts populating state.** Everything the loop writes (`brandsData`, `charges`,
 * `taxArea`, `taxTypes`, `channelvos`, `channelDef`, `categoryGroup`, `categoryData`, `menuData`,
 * `scheduleData`, `channelData`) is a field that platform's own per-section reducers already
 * write, with the same transforms — verified field-by-field against `SharedCatalogState` and
 * against those reducers' bodies before this landed.
 *
 * ⚠️ `hideLoader` is web's signature and is now both platforms'. Every existing caller on the
 * other platform passes nothing, so the default applies; its catalogue saga takes no argument and
 * never reads `action.payload`, so the extra payload is inert there (checked at
 * `apps/mobile/src/reduxStore/saga/pos/catalog.js:58,276`). It is NOT inert on web, where the
 * saga forwards it as the request's `hideLoader`.
 *
 * The `// need to be improved` comment on the completion dispatch is web's own, preserved.
 */
export function getCatalogueDataFn(hideLoader = true) {
  function callOffline() {
    for (let typeObj of othersDataTypes) {
      fetchOthersOffline({
        type: typeObj.type,
        callback: (data: any) => {
          store.dispatch(
            updateCatalogueData({
              data,
              key: typeObj.key,
            })
          );
          if (typeObj.key === othersDataTypes[othersDataTypes.length - 1].key) { // need to be improved. should be called when whole catalog data is fetched
            store.dispatch(catalogueDataCallComplete());
          }
        },
      });
    }
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  // ⚠️ Cast, not a reducer change: `getCatalogueData` is a 0-param reducer on BOTH platforms, so
  // RTK types its creator as taking nothing, while the creator always accepts an argument at
  // runtime. Ledger hazard 14; the standing decision is to cast at the call site rather than
  // widen the reducer. See this function's docstring for what the argument does on each side.
  store.dispatch((getCatalogueData as any)(hideLoader));
}

/**
 * Load the printer list into the printer slice: offline from the local "others" store, online by
 * dispatching the fetch action and letting the saga do it.
 *
 * ⚠️ **Byte-identical in both apps** apart from quote style and arrow-parameter parentheses —
 * diffed body-for-body before collapsing, not assumed. It is the ONLY one of the eighteen
 * `get<Thing>Fn` fetchers that needed no per-platform variant: it dispatches no completion action
 * and takes no loader flag, which is exactly what makes its two siblings in the variant files
 * diverge.
 *
 * ⚠️ Both slice actions come from a BARE folder import, which is what a `shared.ts` must do
 * (hazard 16). Both exist in both variants of `PrinterSlice`.
 */
export function getPrinterDataFn(ip?: any) {
  function callOffline() {
    fetchOthersOffline({
      type: 'printer',
      callback: (printers: any) => {
        store.dispatch(storeAllPrinters(printers));
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(fetchAllPrinter(ip));
}

/**
 * Ends the session: offline sessions are torn down locally, online ones go through the API saga.
 *
 * SHARED: the two apps' bodies are token-for-token the same — the only diff is tabs versus
 * spaces, confirmed by diffing them after whitespace normalisation (7 lines each).
 *
 * ⚠️ `logoutOffline` is a BARE FOLDER import, which is what a `shared.ts` must do (hazard 16) and
 * is load-bearing here rather than a formality: that symbol is PER-VARIANT — this platform's half
 * ends by calling the host logout teardown and the other's does not. Naming a variant here would
 * give one platform the other's teardown.
 *
 * ⚠️ `fetchOthersOffline` above is deliberately imported from `./shared` while this one is not.
 * The rule is per-symbol, not per-module: name `/shared` only when the symbol you want lives
 * there on both platforms.
 *
 * ⚠️ **`ip` is OPTIONAL, and that is not cosmetic.** One app calls `performLogout()` with no
 * argument at all (its account screen has both a with-token and a without-token call site). The
 * app files are `.js`, where a declared-but-omitted parameter is simply `undefined`; core is
 * `.ts`, where a required parameter makes that call `TS2554`. Marking it optional reproduces the
 * JavaScript semantics exactly and is erased at emit.
 *
 * ⚠️ **A second transcription deviation, also runtime-neutral:** both app files call
 * `logoutOffline(ip)` while `logoutOffline` is declared with NO parameters in both apps, so the
 * argument has always been discarded. Those files are `.js`, where that is legal and silent; core
 * is `.ts`, where passing it is `TS2554`. The argument is dropped at the call site rather than
 * inventing a parameter core's `logoutOffline` never had.
 */
export function performLogout(ip?: any) {
  if (isOffline()) {
    logoutOffline();
    return;
  }
  store.dispatch(logoutUser(ip));
}

/**
 * Tell the store a discount edit was closed.
 *
 * Offline this is a NO-OP: the app files both bail out with the comment "not supported in offline
 * mode by rista-library", and that early return is the whole point of routing the call through
 * the facade rather than dispatching directly.
 *
 * SHARED: the two app bodies are token-identical (tabs vs spaces only, diffed after whitespace
 * normalisation). The single textual difference is a stray `// TODO` line one platform carries
 * above the comment; both lines are kept below so neither side loses a note.
 */
export function updateDiscountFn(payload: any) {
  if (isOffline()) {
    // TODO
    // not supported in offline mode by rista-library
    return;
  }
  store.dispatch(updateDiscountClosed(payload));
}

/**
 * Save (hold) the current sale.
 *
 * Offline it goes straight to the local store; online it dispatches `holdSaleData` and the saga
 * does the work. SHARED — the two app bodies are token-identical (tabs vs spaces only).
 *
 * `ip.onOfflineFailure` (decision 95, optional, only v2's discount sheet passes it) is handed to the offline save,
 * which calls it when the local save fails without ever calling `callback`. Online, `ip.handleError` is the
 * saga's failure channel; offline it is NOT used, as two mobile v1 screens pass it and must not change.
 *
 * ⚠️ The offline branch is wrapped in a local `callOffline` that is declared and then called on
 * the very next line. That indirection is the apps' own, on both platforms, and is preserved; the
 * other two `*Fn` below call their offline helper directly, which is the same inconsistency the
 * app files have.
 *
 * ⚠️ **Every offline helper here is imported from the FOLDER, not a variant** — `saveSaleOffline`,
 * `cancelSaleOffline` and `voidSaleOffline` are all per-variant, and a `shared.ts` must let the
 * bundler choose (hazard 16). Naming a variant would give one platform the other's clone
 * function and the other's `statusInfo` shape.
 */
export function saveSaleFn(ip: any) {
  function callOffline() {
    saveSaleOffline({
      sale: ip.dataToSend,
      callback: ip.callback,
      onOfflineFailure: ip.onOfflineFailure,
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(holdSaleData(ip));
}

/**
 * Cancel an order. Offline it rewrites the sale locally; online it dispatches `cancelOrder`.
 *
 * SHARED — token-identical in both apps.
 */
export function cancelOrderFn(ip: any) {
  if (isOffline()) {
    cancelSaleOffline(ip);
    return;
  }
  store.dispatch(cancelOrder(ip));
}

/**
 * Void an order. Offline it rewrites the sale locally; online it dispatches `voidOrder`.
 *
 * SHARED — token-identical in both apps.
 */
export function voidOrderFn(ip: any) {
  if (isOffline()) {
    voidSaleOffline(ip);
    return;
  }
  store.dispatch(voidOrder(ip));
}


/**
 * Route a fulfillment-status update to the API or to the offline store.
 *
 * Uplifted 2026-09-08 for the `MyOrderCard` intent layer. The two apps' bodies were
 * BYTE-IDENTICAL apart from tabs-vs-spaces, so there was nothing to converge — it sits in
 * `shared.ts` and both platforms get the same one.
 *
 * ⚠️ The offline branch RETURNS EARLY and never dispatches. That is the whole point of the
 * facade: offline, the sale is stamped and queued locally by `updateFulfillmentStatusOffline`,
 * and the saga that would have called the API must not also run.
 */
export function updateFulfillmentStatementFn(ip: any) {
  if (isOffline()) {
    updateFulfillmentStatusOffline(ip);
    return;
  }
  store.dispatch(updateOrderStatus(ip));
}

// ── My-orders list / detail / save (uplifted 2026-09-15, docs/MY_ORDERS_V2_PLAN.md §4) ────────
// The three the v2 ViewModel needs. Diffed both apps' bodies first: TOKEN-IDENTICAL, including
// the instore filter rewrite the plan suspected was web-only. Mobile's copy of `getOpenOrderFn`
// also logged `isOffline()` on entry; that log is the only thing not carried over.

/**
 * Fetch an instore order list. Offline it queries the local store and dispatches the SUCCESS
 * action directly (so the saga never runs); online it dispatches the request.
 *
 * ⚠️ The offline branch MUTATES `ip.data` before querying: the API's `saleStatus` becomes the
 * local store's `status`, except that "Cancelled" is expressed as a `filterType` and
 * "Amount Due" as `filterType: "amountDue"` + `status: "Closed"`. Both apps do exactly this;
 * v2 builds a fresh `getSendData()` per call, so the mutation never leaks into a second request.
 * Offline results are REVERSED (newest first) — the local store returns them oldest first.
 */
export function getOpenOrderFn(ip: any) {
  if (isOffline()) {
    // the rewrite is `myOrders/buckets.instoreLocalQuery` — one definition, shared with v2's own
    // offline lane (`myOrders/localOrders.ts`); assigned over the payload, as this always did
    Object.assign(ip.data, instoreLocalQuery(ip.data));

    filterRemoteStore({
      filter: ip,
      cb: (res: any) => {
        if (res?.results?.length) res.results.reverse();
        store.dispatch(getOpenOrderDataSuccess(res));
      },
    });
    return;
  }
  store.dispatch(getOpenOrderData(ip));
}

/**
 * The my-orders SEARCH (simple and advanced modes). Offline: the local store filtered by the
 * same body, dispatched straight into `getSearchOrdersSuccess`; online: the saga. Token-identical
 * in both apps; moved 2026-09-17 (plan §7 phase 6 slice 2).
 */
export function getMyOrderSearchFn(sendObj: any) {
  if (isOffline()) {
    filterRemoteStore({
      filter: { data: sendObj },
      cb: (res: any) => {
        store.dispatch(getSearchOrdersSuccess(res));
      },
    });
    return;
  }
  store.dispatch(getSearchOrders(sendObj));
}

/** Fetch one order's detail. Offline: the local sale, via `ip.callback`; online: the saga. */
export function fetchMyOrderDataFn(ip: any) {
  if (isOffline()) {
    fetchOfflineSale(ip);
    return;
  }
  store.dispatch(fetchOrderData(ip));
}

/**
 * Save an order from the my-orders flow. Same shape as `saveSaleFn` above but dispatches
 * `saveMyorder`, whose saga handles the back-order modal and the offline-sync record on web.
 */
export function myOrderSaveSaleFn(ip: any) {
  if (isOffline()) {
    saveSaleOffline({
      sale: ip.dataToSend,
      callback: ip.callback,
    });
    return;
  }
  store.dispatch(saveMyorder(ip));
}

// ── Order detail: close and print bill (uplifted 2026-09-15, plan §7 phase 2) ─────────────────

/**
 * Close an order. Offline it closes the local sale (`saleCloseOffline`) and toasts; online it
 * dispatches `closeOrder`. WEB's body: mobile had the two offline toasts commented out and
 * gains them through the bridge (plan §3, "web behaviour mobile lacked").
 */
export function closeOrderFm(ip: any) {
  if (isOffline()) {
    saleCloseOffline({
      sale: ip.dataToSend,
      callback: (response: any) => {
        if (response && ip.callback) ip.callback(response);
        if (!response?.code) notifySuccess(ORDER_CLOSED);
        if (response?.code === REST_SALE_ITEMS_ON_HOLD) notifyError((backendErrorMap as any)?.REST_SALE_ITEMS_ON_HOLD);
      },
    });
    return;
  }
  store.dispatch(closeOrder(ip));
}

/**
 * Print a bill. Offline the sale is saved locally with `opType: "Print"` and `printSuccess` is
 * dispatched with the result; online the `print` saga does both. TOKEN-IDENTICAL in both apps.
 */
export function printBillFunction(ip: any) {
  if (isOffline()) {
    const cloneOrder = cloneDeep(ip.dataToSend); // Hermes has no structuredClone; saveSaleOffline re-clones per platform before writing
    cloneOrder["opType"] = "Print";
    saveSaleOffline({
      sale: cloneOrder,
      callback: (res: any) => {
        if (ip.callback) ip.callback(res);
        store.dispatch(printSuccess(res));
      },
    });
    return;
  }
  store.dispatch(print(ip));
}
